import styles from './StatusIndicator.module.css';

/** The §2 tones a state can carry. */
export const STATUS_TONES = ['positive', 'notice', 'danger', 'neutral'] as const;
export type StatusTone = (typeof STATUS_TONES)[number];

/**
 * The dot's shape, and it is a required channel rather than decoration: §2's build rule is that no
 * state is ever carried by colour alone, so the shape has to distinguish the states even in
 * greyscale, and even for a reader who cannot resolve the hues.
 */
export const STATUS_SHAPES = ['solid', 'ring', 'half'] as const;
export type StatusShape = (typeof STATUS_SHAPES)[number];

export interface StatusIndicatorProps {
    readonly tone: StatusTone;
    readonly shape: StatusShape;
    /** The short visible label — `Fresh`, `Rotting · 12d`, `P1`. Required, and non-empty. */
    readonly label: string;
    /** The long form, when the short one is an abbreviation. Becomes the accessible name (§5). */
    readonly description?: string;
    readonly 'data-testid'?: string;
}

/**
 * A dot plus a **visible text label**, naming a *state* — distinct from Badge, which is a filled
 * pill naming a *category* (§5 draws that line explicitly).
 *
 * FS-3 AC-17 and §2's build rule are enforced twice over, because "colour only" is a regression that
 * ships silently: `label` is required by the type, and an empty or whitespace-only one throws
 * rather than rendering a bare dot. A component that *can* render colour-alone eventually will,
 * usually on the one screen nobody re-checked; making it unrepresentable is cheaper than
 * remembering. Same move as the discriminated union behind Button's icon-only family.
 *
 * `role="img"` with the long form as the accessible name is §5's rule — "the label is the short
 * form; the long form goes in the accessible name". The role also stops a screen reader reading the
 * abbreviation *and* its expansion, which is what happens if both are left in the tree.
 *
 * **No state vocabulary is encoded here, deliberately.** §5 tabulates states (`Fresh`, `Ageing`,
 * `Blocked`/`Slipping`) but flags the wording as provisional wherever the PRD names none — open item
 * 14, still awaiting Empire's own language. Only the STRUCTURE is settled, so this component ships
 * tone × shape × label and the caller supplies the words. Encoding the table now would bake
 * unconfirmed vocabulary into a foundation component and make it expensive to correct.
 */
export function StatusIndicator({
    tone,
    shape,
    label,
    description,
    'data-testid': testId,
}: StatusIndicatorProps) {
    if (label.trim().length === 0) {
        throw new Error(
            'StatusIndicator needs a visible label: a status carried by the dot alone is invisible ' +
                'in greyscale and to a colour-blind reader (design-system §2, FS-3 AC-17).',
        );
    }

    return (
        <span aria-label={description ?? label} className={styles.indicator} data-testid={testId} role="img">
            <span aria-hidden="true" className={`${styles.dot} ${styles[shape]} ${styles[tone]}`} />
            <span className={styles.label}>{label}</span>
        </span>
    );
}
